import { useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { ModalCWShellUI } from '@next-order/next-order-components';
import { eventNames } from 'configuration/eventNames';
import { MenuContext } from 'contexts/MenuProvider';
import { MENU_ITEM_OBJ_TYPE } from 'helpers/enums';
import getImage from 'helpers/getImage';
import { usePromoConfetti } from 'helpers/hooks/usePromoConfetti';


const BADGE_COLORS = [
  'bg-primary/10 text-primary',
  'bg-purple-500/10 text-purple-700',
  'bg-yellow-400/20 text-yellow-700',
  'bg-red-500/10 text-red-700',
];

const DISMISS_TEXT = 'Close';
const CLOSE_ANIMATION_MS = 300;
const SCROLL_DELAY_MS = 300;
const HIGHLIGHT_DURATION_MS = 3000;

let hasShownThisPageLoad = false;
const asText = (value) => (typeof value === 'string' ? value.trim() : '');

const parseHttpUrl = (action) => {
  if (typeof action !== 'string' || !action.trim()) return null;

  try {
    const url = new URL(action.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch (_err) {
    return null;
  }
};

const ID_PARAMS = ['specialId', 'menuItemId'];

const readIdParam = (url) => {
  for (const param of ID_PARAMS) {
    const id = url.searchParams.get(param)?.trim();
    if (id) return id;
  }
  return '';
};

const hasIdParam = (url) =>
  ID_PARAMS.some((param) => url.searchParams.has(param));

const toBadgeList = (badges) =>
  (Array.isArray(badges) ? badges : [])
    .map((badge) => (typeof badge === 'string' ? badge.trim() : ''))
    .filter(Boolean);

const readEffects = (effects) => {
  const on = new Set(Array.isArray(effects) ? effects : []);

  return {
    confetti: on.has('confetti'),
    heading: on.has('headingHighlight'),
    underline: on.has('textUnderline'),
    aurora: on.has('auroraGlow'),
    badge: on.has('badgeEffect'),
    button: on.has('buttonEffect'),
    cross: !on.has('hideCross'),
  };
};

const markClassName = (highlight, underline) => {
  if (highlight && underline) return 'promo-sheet-mark promo-sheet-mark--both';
  if (highlight) return 'promo-sheet-mark promo-sheet-mark--highlight';
  if (underline) return 'promo-sheet-mark promo-sheet-mark--underline';
  return '';
};

const eventFor = (itemObjType) =>
  itemObjType === MENU_ITEM_OBJ_TYPE.special
    ? eventNames.SPECIALS_MENU_ITEM_OPENED
    : eventNames.MENU_ITEM_OPENED;

function NotificationModal({
  orderSetup,
  searchCategoriesList,
  onClickItemHandler,
}) {
  const {
    isNotificationOn,
    notificationImage,
    notificationImagePath,
    notificationTitle,
    notificationMessage,
    notificationButtonText,
    notificationButtonAction,
    notificationBadges,
    notificationModalEffects,
  } = orderSetup || {};
  const { setHighlightedItemId } = useContext(MenuContext);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const fallbackTried = useRef(false);
  const image = asText(notificationImagePath) || asText(notificationImage);
  const badges = toBadgeList(notificationBadges);
  const fx = readEffects(notificationModalEffects);
  const markClass = markClassName(fx.heading, fx.underline);

  usePromoConfetti(showModal && fx.confetti);

  useEffect(() => {
    if (hasShownThisPageLoad) return;

    if (isNotificationOn && notificationMessage) {
      hasShownThisPageLoad = true;
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotificationOn, notificationMessage]);

  useEffect(() => {
    if (!isClosing) return undefined;

    const timer = setTimeout(() => {
      setShowModal(false);
      setIsClosing(false);
    }, CLOSE_ANIMATION_MS);

    return () => clearTimeout(timer);
  }, [isClosing]);

  const closeModal = () => {
    setIsClosing(true);
  };

  const handleOpenChange = (open) => {
    if (!open) closeModal();
  };

  const findTarget = (id) =>
    (searchCategoriesList ?? [])
      .flatMap((category) => category?.data ?? [])
      .find((item) => item?._id === id);

  const openItem = (item) => {
    onClickItemHandler(item.itemObjType, item, eventFor(item.itemObjType));

    setTimeout(() => {
      const itemElement = document.querySelector(
        `[data-menu-item-id="${item._id}"]`
      );

      if (itemElement) {
        setHighlightedItemId(item._id);

        itemElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });

        setTimeout(() => {
          setHighlightedItemId(null);
        }, HIGHLIGHT_DURATION_MS);
      }
    }, SCROLL_DELAY_MS);
  };

  const handleButtonClick = () => {
    const action = asText(notificationButtonAction);
    const url = parseHttpUrl(action);
    const target = url ? findTarget(readIdParam(url)) : findTarget(action);

    if (target) {
      openItem(target);
    } else if (url && !hasIdParam(url)) {
      window.open(url.href, '_blank', 'noopener,noreferrer');
    }

    closeModal();
  };

  return (
    <>
      {showModal && (
        <ModalCWShellUI
          maxWidth={460}
          open={showModal}
          onOpenChange={handleOpenChange}
          zIndex={9999}
          maxHeight='full'
          isLoading={!!image && isLoading}
          extendedClassName={isClosing ? '!animate-pm-fade-out' : ''}
          extendedOverlayClassName={`backdrop-blur-[3px] ${
            isClosing ? '!animate-pm-fade-out' : ''
          }`}
        >
          {fx.aurora && (
            <div
              aria-hidden
              className='pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]'
            >
              <div className='promo-sheet-aurora__blob promo-sheet-aurora__blob--one' />
              <div className='promo-sheet-aurora__blob promo-sheet-aurora__blob--two' />
              <div className='promo-sheet-aurora__blob promo-sheet-aurora__blob--three' />
            </div>
          )}

          {fx.cross && (
            // the library pins this top-left, where it would land on the first
            // badge; !left-auto beats its own left-4.5 in the same class string
            <ModalCWShellUI.CloseButton
              onClick={closeModal}
              extendedClassName='!left-auto right-4.5'
            />
          )}

          <div
            className={`relative flex shrink-0 flex-wrap items-center gap-2 px-5 pt-5 ${
              fx.cross ? 'min-h-[58px] pr-14 sm:min-h-[52px]' : ''
            }`}
          >
            {badges.map((badge, index) => (
              <span
                key={`${badge}-${index}`}
                className={`rounded-full px-2.75 py-1.5 text-[11px] font-extrabold leading-none tracking-[0.1em] ${
                  fx.badge ? 'promo-sheet-badge' : ''
                } ${BADGE_COLORS[index % BADGE_COLORS.length]}`}
              >
                {badge}
              </span>
            ))}
          </div>

          {notificationTitle && (
            <h2
              className={`relative mb-0 px-5 pt-2.5 text-center text-[29px] font-extrabold tracking-[-0.03em] text-[#101620] ${
                markClass ? 'leading-[1.34]' : 'leading-[1.12]'
              }`}
            >
              <span className={markClass}>{notificationTitle}</span>
            </h2>
          )}
          <div className='relative z-10 flex max-h-full w-full flex-col overflow-hidden'>
            <div className='block h-full w-full scroll-smooth overflow-y-auto overflow-x-hidden px-5 pb-2 pt-2.5'>
              {notificationMessage && (
                <p className='mx-auto mb-0 max-w-[300px] text-center text-sm leading-[1.5] text-[#6f7787] [text-wrap:pretty]'>
                  {notificationMessage}
                </p>
              )}

              {!!image && (
                <div className='relative mt-4 w-full overflow-hidden rounded-[22px] bg-[#e9e7e2]'>
                  <img
                    src={getImage(image)}
                    alt='notification'
                    className='block h-auto max-h-[70vh] w-full object-contain'
                    onError={(e) => {
                      setIsLoading(false);

                      const fallback = getImage(asText(notificationImage));

                      if (
                        !fallbackTried.current &&
                        fallback &&
                        fallback !== e.target.src
                      ) {
                        fallbackTried.current = true;
                        e.target.src = fallback;
                      }
                    }}
                    onLoad={() => {
                      setIsLoading(false);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className='relative z-20 px-5 pb-4.5 pt-3.5 sm:pt-4.5'>
            <div className='w-full'>
              {notificationButtonText && (
                // buttonEffect drives all three CTA animations together: the
                // float, the sheen sweep and the glow pulse.
                <div
                  className={
                    fx.button
                      ? 'motion-safe:animate-pm-cta-float motion-safe:hover:[animation-play-state:paused]'
                      : ''
                  }
                >
                  <div
                    className={`promo-sheet-cta relative rounded-xl ${
                      fx.button ? 'promo-sheet-cta--glow' : ''
                    }`}
                  >
                    <ModalCWShellUI.Button
                      onClick={handleButtonClick}
                      extendedClassName='!font-extrabold !tracking-[-0.01em]'
                    >
                      {notificationButtonText}
                    </ModalCWShellUI.Button>
                    {fx.button && (
                      <div
                        aria-hidden
                        className='pointer-events-none absolute inset-0 overflow-hidden rounded-xl'
                      >
                        <div className='absolute inset-y-0 left-0 w-2/5 bg-gradient-to-r from-transparent via-[rgba(255,255,255,0.35)] to-transparent motion-safe:animate-pm-sheen' />
                      </div>
                    )}
                  </div>
                </div>
              )}
              <button
                type='button'
                onClick={closeModal}
                className={`w-full py-1.5 text-center text-[15px] font-semibold text-[#9aa1ad] transition-colors hover:text-[#6f7787] ${
                  notificationButtonText ? 'pt-3' : ''
                }`}
              >
                {DISMISS_TEXT}
              </button>
            </div>
          </div>
        </ModalCWShellUI>
      )}
    </>
  );
}

function mapStateToProps(state) {
  const {
    homeReducer: { orderSetup },
  } = state;

  return {
    orderSetup,
  };
}

export default connect(mapStateToProps, null)(NotificationModal);
